import { MutableRefObject, useEffect, useRef } from "react";
import { formatISO } from "date-fns";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import { ACTIVE_USER_ID_KEY } from "@constants";
import { safeLocalStorage, safeSessionStorage } from "@helpers/storage";
import { recordOrganizationClientAccess } from "@api/organizationClient.api";

const getClientAccessSessionKey = (userId: string, workspaceId: string) =>
    `${userId}-client-access-${workspaceId}`;

interface UseRecordOrganizationClientAccessParams {
    organizationId?: string | null;
    workspaceId?: string | null;
    pendingSourceRef: MutableRefObject<OrganizationMemberClientAccessSource | null>;
}

/**
 * Single writer for the "organization member entered a client" audit row.
 *
 * Fires on two triggers:
 *  1. Change — the active workspace changed from one client to another.
 *  2. Hydrate — first time this browser tab session lands in a given client
 *     (deep link, last-active hydrate on cold start).
 *
 * A per-tab sessionStorage key gates the hydrate so a refresh in the same tab
 * does not re-log, while a fresh tab (empty sessionStorage) logs one hydrate.
 * The key is set synchronously before the request so Strict Mode double-mount
 * or a later `initializeScope` of the same workspace cannot double-log. A failed
 * request clears the key again so a later refresh/remount can retry — recording
 * never blocks navigation.
 *
 * A hydrate is only logged when an explicit scope operation supplied a source:
 * the initial seeded render (which reads the last-active workspace from storage
 * before the app initializer confirms it) carries no pending source, so it never
 * logs a phantom row for a workspace the member has not actually entered.
 */
export function useRecordOrganizationClientAccess({
    organizationId,
    workspaceId,
    pendingSourceRef,
}: UseRecordOrganizationClientAccessParams): void {
    const previousWorkspaceIdRef = useRef<string | null>(null);

    useEffect(() => {
        const previousWorkspaceId = previousWorkspaceIdRef.current;
        previousWorkspaceIdRef.current = workspaceId ?? null;

        const pendingSource = pendingSourceRef.current;
        pendingSourceRef.current = null;

        if (!organizationId || !workspaceId) {
            return;
        }

        const userId = safeLocalStorage.getItem(ACTIVE_USER_ID_KEY);
        if (!userId) {
            return;
        }

        const isChange =
            previousWorkspaceId != null && previousWorkspaceId !== workspaceId;

        const sessionKey = getClientAccessSessionKey(userId, workspaceId);
        const hasLoggedThisTabSession =
            safeSessionStorage.getItem(sessionKey) != null;

        const isHydrate = !hasLoggedThisTabSession && pendingSource != null;

        if (!isChange && !isHydrate) {
            return;
        }

        const source =
            pendingSource ??
            (isChange
                ? OrganizationMemberClientAccessSource.OTHER
                : OrganizationMemberClientAccessSource.HYDRATE);

        // Hydrate rows never carry a previousWorkspaceId: a cold-start hydrate
        // can look like a "change" relative to the seeded workspace, but that
        // seed is not a real prior client the member navigated from.
        const isHydrateSource =
            source === OrganizationMemberClientAccessSource.HYDRATE;
        const previousWorkspaceIdToRecord =
            isChange && !isHydrateSource ? previousWorkspaceId : null;

        safeSessionStorage.setItem(sessionKey, formatISO(new Date()));

        void recordOrganizationClientAccess({
            organizationId,
            payload: {
                clientWorkspaceId: workspaceId,
                previousWorkspaceId: previousWorkspaceIdToRecord,
                source,
            },
        }).catch(() => {
            // A failed write must not permanently suppress later attempts in
            // this tab: clear the marker so a subsequent refresh/remount retries.
            safeSessionStorage.removeItem(sessionKey);
        });
    }, [organizationId, workspaceId, pendingSourceRef]);
}
